import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';
const compareObjects = require('fast-deep-equal');

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import * as reactGlobalClick from '../../../_common/platform/ts/reactGlobalClick';

import ParameterFieldBase, {
    IParameterFieldBaseOptions,
    IValidator
} from '../../../_common/platform/ts/ParameterFieldBase';

import { ProgressBar } from '../../../_common/platform/tsx/ProgressBar';
import { IPopupComponentProperties, IPopupOptions } from '../../../pdc-platform/src/ts/actions/DialogActions';
import { IDisplayValue, IPosition, ISize, IWidth } from '../../../_common/platform/ts/Interfaces';

import { AttributeValueSortingType } from '../../../pdc-platform/src/ts/stores/EntityModelsStore';

import { IQueryFilter, IGlobalFilterProperties, ITreeDataQueryData } from '../../platform/tsx/filtering/Interfaces';

import { IValuesCollection } from '../../platform/ts/appTheme';
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';
import { useDropdownSelectorStateHook } from './selectors/Hooks';
import { SelectedItemsDisplayStatelessComponent } from './selectors/SelectedItemsDisplayComponent';
import { getFirstTreeItem, getTreeValuesFilteredByGlobalFilter } from './filtering/AttributeFiltersCreator';

import {
    CLEAR_SELECTIONS_VALUE,
    getSearchHighlightingColor,
    NoItemElement,
    SearchField,
    SHOW_MORE_VALUE
} from './selectors/Buttons';

import {
    ClearSelectionsItem,
    findNodeIndexByValue,
    findNodesByID,
    IExpandedStates,
    IItemReferences,
    ITreeItem,
    ITreeSearchNode,
    nodeIsSelected,
    ShowMoreItem,
    TreeSelectItem
} from './selectors/TreeSelectItem';

import { ITreeNode, ITreeValues } from '../../../pdc-platform/src/ts/Interfaces';

// Thresholds are different because items are used to collapsed but are all expanded on searching.
const SUGGESTIONS_LIST_THRESHOLD = 200;
const SUGGESTIONS_SEARCH_LIST_THRESHOLD = 100;

export interface ITreeSelectOptions extends IParameterFieldBaseOptions {}

export interface ITreeSelectProperties {
    value?: IDisplayValue | IDisplayValue[];
    dataSourceID?: number;
    attributeData?: ITreeDataQueryData;
    allowNull?: boolean;
    listThreshold?: number;
    boldParentNodes?: boolean;
    notSelectableParentNodes?: boolean;
    autoSelectChildNodes?: boolean;
    selectFirstItemByDefault?: boolean;
    allowMultiselection?: boolean;
    placeholder?: string;
    usePlaceholderAsTitle?: boolean;
    width?: IWidth;
    minWidth?: IWidth;
    theme: IValuesCollection;
    localFilter?: IQueryFilter[];
    tabIndex?: number;
    sortingType?: AttributeValueSortingType;
    globalFilter?: IGlobalFilterProperties;
    options?: ITreeSelectOptions;
    hideSelectAllAndClear?: boolean;
    hideSearchField?: boolean;
    name?: string;
    readOnly?: boolean;
    showTreeOnly?: boolean;
    minSelectedItemsCellWidth?: number;
    glob: IGlob;
    getValues?(): JQueryDeferred<ITreeValues>;
    loadSubNodes?(item: ITreeNode): JQueryPromise<any>;
    onChange?(value: IDisplayValue[]): void;
}

interface IDropdownOptions extends IPopupOptions {
    value: IDisplayValue[];
    listThreshold: number;
    loading: boolean;
    boldParentNodes: boolean;
    notSelectableParentNodes: boolean;
    autoSelectChildNodes: boolean;
    selectFirstItemByDefault: boolean;
    expandedStates: IExpandedStates;
    allowMultiselection: boolean;
    allowNull: boolean;
    theme: IValuesCollection;
    defaultSearchString: string;
    hideSelectAllAndClear: boolean;
    hideSearchField: boolean;
    updateState: (callback: (obj) => void) => void;
    onInit(): void;
    onChange(value: IDisplayValue[]): void;
    onLoadSubNodes?(item: ITreeNode): void;
    onClose?(expandedItems: IExpandedStates): void;
}

interface IValuesState {
    loading?: boolean;
    valuesTree?: ITreeValues;
}

interface ITreeDropdownProperties extends IPopupComponentProperties {
    options: IDropdownOptions;
}

function selectChildNodes(checked: boolean, node: ITreeNode, result: ITreeItem[]) {
    if (node != null && node.subNodes != null) {
        node.subNodes.forEach((subNode) => {
            const index = findNodeIndexByValue(subNode.id, result);

            if (checked) {
                if (index < 0) {
                    result.push({ key: undefined, value: subNode.id, displayValue: subNode.title });
                }
            } else if (result.length > 0) {
                result.splice(index, 1);
            }

            selectChildNodes(checked, subNode, result);
        });
    }
}

function findTreeNode(value: string, tree: ITreeNode[], check: (item: ITreeNode, value: string) => boolean): ITreeNode {
    for (let i = 0; i < tree.length; i++) {
        let result = tree[i];

        if (result != null) {
            if (check(result, value)) {
                return result;
            } else if (result.subNodes != null && result.subNodes.length > 0) {
                result = findTreeNode(value, result.subNodes, check);

                if (result != null) {
                    return result;
                }
            }
        }
    }

    return null;
}

export function findTreeNodeByKey(key: string, tree: ITreeNode[]): ITreeNode {
    return findTreeNode(key, tree, (item: ITreeNode, value: string) => item.key === value);
}

export function findTreeNodeByID(id: string, tree: ITreeNode[]): ITreeNode {
    return findTreeNode(id, tree, (item: ITreeNode, value: string) => item.id === value);
}

function searchInTreeNodes(
    nodes: ITreeNode[],
    searchString: string,
    fromStartOnly: boolean,
    matches: ITreeItem[],
    notSelectableParentNodes: boolean
): ITreeSearchNode[] {
    const result = nodes?.reduce<ITreeSearchNode[]>((acc, node) => {
        const subNodes = searchInTreeNodes(
            node.subNodes,
            searchString,
            fromStartOnly,
            matches,
            notSelectableParentNodes
        );
        const title = node.title;
        const searchLength = searchString.length;
        const valueIndex =
            (!notSelectableParentNodes || node.subNodes == null || node.subNodes.length === 0) &&
            searchLength > 0 &&
            title != null &&
            title !== ''
                ? title.toLowerCase().indexOf(searchString)
                : -1;

        if (valueIndex === 0 || (!fromStartOnly && valueIndex > 0) || subNodes?.length > 0) {
            // Create a new node with only subnodes that match the search string.
            const exactMatchExists = searchLength > 0 && valueIndex >= 0;
            const newNode: ITreeSearchNode = {
                ...node,
                subNodes: subNodes,
                begining: valueIndex > 0 ? title.slice(0, valueIndex) : undefined,
                exactMatch: exactMatchExists ? title.slice(valueIndex, valueIndex + searchLength) : undefined,
                ending: title.slice(valueIndex + searchLength)
            };

            if (exactMatchExists) {
                matches.push({ value: node.id, displayValue: title, key: node.key });
            }

            acc.push(newNode);
        }

        return acc;
    }, []);

    return result;
}

function truncateSubItemsToShow(
    subItems: ITreeNode[],
    totalCount: number,
    addToItemsToShow: boolean,
    expandAll: boolean,
    expandedStates: IExpandedStates,
    selectedItems: ITreeItem[]
): { itemsToShow: ITreeNode[]; totalCount: number; visibleItemsCount: number } {
    const itemsToShow: ITreeNode[] = [];

    let newTotalCount = totalCount;
    let visibleItemsCount = 0;

    subItems?.forEach((itemToShow) => {
        const subNodesResult = truncateSubItemsToShow(
            itemToShow.subNodes,
            newTotalCount,
            addToItemsToShow,
            expandAll,
            expandedStates,
            selectedItems
        );

        newTotalCount = subNodesResult.totalCount + 1;

        if (addToItemsToShow) {
            visibleItemsCount++;

            itemsToShow.push({
                ...itemToShow,
                subNodes: subNodesResult.itemsToShow
            });

            let expanded = expandAll;

            if (!expanded) {
                expanded = expandedStates[itemToShow.key];

                if (expanded == null) {
                    expanded = nodeIsSelected(itemToShow.key, itemToShow.id, selectedItems);
                }
            }

            if (expanded) {
                visibleItemsCount += subNodesResult.visibleItemsCount;
            }
        }
    });

    return {
        itemsToShow: itemsToShow,
        totalCount: newTotalCount,
        visibleItemsCount: visibleItemsCount
    };
}

// Items To Show - the items that user can see without clicking Show More.
// Visible Items - the shown root items and the sub items having all their parent items expanded.
function truncateItemsToShow(
    items: ITreeNode[],
    listThreshold: number,
    expandAll: boolean,
    expandedStates: IExpandedStates,
    selectedItems: ITreeItem[]
): { itemsToShow: ITreeNode[]; totalCount: number; visibleItemsCount: number } {
    const itemsToShow: ITreeNode[] = [];

    let totalCount = 0;
    let visibleItemsCount = 0;
    let addToItemsToShow = true;

    for (let i = 0; i < items.length; i++) {
        const itemToShow = items[i];
        const subNodesResult = truncateSubItemsToShow(
            itemToShow.subNodes,
            totalCount,
            addToItemsToShow,
            expandAll,
            expandedStates,
            selectedItems
        );

        totalCount = subNodesResult.totalCount + 1;

        if (addToItemsToShow) {
            // If a root node is added, then all its sub nodes are added.
            // Otherwise, Show More must be implemented for each branch.
            itemsToShow.push({
                ...itemToShow,
                subNodes: subNodesResult.itemsToShow
            });
            visibleItemsCount++;

            let expanded = expandAll;

            if (!expanded) {
                expanded = expandedStates[itemToShow.id] === true;
            }

            if (expanded) {
                visibleItemsCount += subNodesResult.visibleItemsCount;
            }

            if (visibleItemsCount > listThreshold) {
                addToItemsToShow = false;
            }
        }
    }

    return {
        itemsToShow: itemsToShow,
        totalCount: totalCount,
        visibleItemsCount: visibleItemsCount
    };
}

function handleOnContainerClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    reactGlobalClick.cancel();
    e.stopPropagation();
    e.preventDefault();
}

function TreeDropdown(props: ITreeDropdownProperties) {
    const searchFieldIsFocused = React.useRef(false);
    const shownItems = React.useRef<ITreeItem[]>(null);
    const focusedItem = React.useRef<ITreeItem>(null);
    const expandedStates = React.useRef<IExpandedStates>({ ...props.options.expandedStates });
    const matches = React.useRef<ITreeItem[]>([]);

    const [values, setValues] = React.useState(
        props.options.value?.map((value) => {
            return { key: undefined, value: value.value, displayValue: value.displayValue } as ITreeItem;
        })
    );

    const [listThreshold, setListThreshold] = React.useState(props.options.listThreshold ?? SUGGESTIONS_LIST_THRESHOLD);
    const [searchListThreshold, setSearchListThreshold] = React.useState(
        props.options.listThreshold ?? SUGGESTIONS_SEARCH_LIST_THRESHOLD
    );
    const [searchString, setSearchString] = React.useState(props.options.defaultSearchString ?? '');
    const [valuesState, setValuesState] = React.useState<IValuesState>({
        loading: props.options.loading
    });
    const [, setUpdateFlag] = React.useState(0);

    const itemsRef = React.useRef<IItemReferences>({});
    const emptyItemRef = React.useRef<HTMLLIElement>(null);
    const searchFieldRef = React.useRef<HTMLInputElement>(null);
    const treeControlRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        props.options.updateState((obj: IValuesState) => {
            try {
                setValuesState(obj);
            } catch (e) {}
        });

        if (props.options.onInit != null) {
            props.options.onInit();
        }
    }, [props.options.updateState, props.options.onInit]);

    React.useEffect(() => {
        if (focusedItem.current?.key != null) {
            itemsRef.current[focusedItem.current.key]?.focus?.();
        } else if (searchFieldIsFocused.current) {
            searchFieldRef?.current?.focus?.();
        } else if (emptyItemRef.current != null) {
            // Focus is set to Empty Item to make KeyDown work.
            emptyItemRef.current.focus?.();
        } else if (treeControlRef.current != null) {
            treeControlRef.current.focus?.();
        }
    });

    const processOnClose = React.useCallback(() => {
        if (props.options.onClose != null) {
            props.options.onClose({
                ...expandedStates.current
            });
        }

        if (props.close != null) {
            props.close();
        }
    }, [props.options.onClose, props.close]);

    const handleOnClose = React.useCallback(() => {
        processOnClose();
    }, [processOnClose]);

    reactGlobalClick.useGlobalClickHook(handleOnClose, 1);

    const processValueChange = React.useCallback(
        (value: ITreeItem[]) => {
            if (props.options.allowNull !== false || value?.length > 0) {
                setValues(value);

                if (props.options.onChange != null) {
                    const result: IDisplayValue[] = [];

                    if (value != null) {
                        value.forEach((item) => {
                            if (result.findIndex((addedItem) => addedItem.value === item.value) < 0) {
                                result.push({ value: item.value, displayValue: item.displayValue });
                            }
                        });
                    }

                    props.options.onChange(result);
                }
            }
        },
        [props.options.allowNull, props.options.onChange]
    );

    const clearSelections = React.useCallback(() => {
        if (values == null || (values.length < 2 && props.options.allowNull === false)) {
            return;
        }

        const newValues: ITreeItem[] = [];

        if (
            props.options.allowMultiselection &&
            searchString.length > 0 &&
            values.length > 0 &&
            matches.current.length > 0
        ) {
            // Clear the filtered values only.
            values.forEach((value) => {
                if (matches.current.findIndex((match) => match.value === value?.value) < 0) {
                    newValues.push(value);
                }
            });
        }

        if (props.options.allowNull === false && newValues.length === 0) {
            if (values.length > 0) {
                newValues.push(values[0]);
            } else if (focusedItem.current != null) {
                newValues.push({ ...focusedItem.current });
            }
        }

        processValueChange(newValues.length > 0 ? newValues : null);
    }, [processValueChange, values, props.options.allowNull, props.options.allowMultiselection]);

    const handleOnCheckedChanged = (checked: boolean, item: ITreeItem) => {
        const { key, value } = item;

        if (checked && props.options.notSelectableParentNodes) {
            const treeNode = findTreeNodeByKey(key, valuesState.valuesTree.tree);

            if (treeNode.subNodes?.length > 0) {
                return;
            }
        }

        const newItem: ITreeItem = {
            key: undefined,
            value: value,
            displayValue: item.displayValue
        };

        let newValues: ITreeItem[];

        if (props.options.allowMultiselection) {
            if (values != null) {
                newValues = [...values];

                const index = findNodeIndexByValue(value, newValues);

                if (checked) {
                    if (index < 0) {
                        newValues.push(newItem);
                    }
                } else if (index > -1) {
                    newValues.splice(index, 1);

                    if (newValues.length === 0) {
                        newValues = null;
                    }
                }
            } else if (checked) {
                newValues = [newItem];
            }

            if (props.options.autoSelectChildNodes) {
                const treeNodes = findNodesByID(value, valuesState.valuesTree.tree);

                if (newValues == null) {
                    newValues = [];
                }

                treeNodes.forEach((treeNode) => {
                    selectChildNodes(checked, treeNode, newValues);
                });
            }
        } else {
            newValues = checked ? [newItem] : [];
        }

        if (focusedItem.current == null || focusedItem.current.key !== item.key) {
            focusedItem.current = { ...item };
        }

        processValueChange(newValues);
    };

    const handleOnTreeItemSelect = (node: ITreeItem, close = true) => {
        const isSelected = nodeIsSelected(node.key, node.value, values);

        handleOnCheckedChanged(!isSelected, node);

        if (close) {
            processOnClose();
        }
    };

    const processOnShowMoreClick = React.useCallback(
        (isSearching: boolean) => {
            if (isSearching) {
                setSearchListThreshold(searchListThreshold + SUGGESTIONS_SEARCH_LIST_THRESHOLD);
            } else {
                setListThreshold(listThreshold + SUGGESTIONS_LIST_THRESHOLD);
            }
        },
        [searchListThreshold, listThreshold]
    );

    const processOnEnterPress = () => {
        const currentItem = focusedItem.current;
        const currentItemValue = currentItem?.value;

        if (currentItemValue === CLEAR_SELECTIONS_VALUE) {
            clearSelections();
        } else if (currentItemValue === SHOW_MORE_VALUE) {
            processOnShowMoreClick(searchString.length > 0);
        } else {
            if (!searchFieldIsFocused.current && currentItem != null) {
                handleOnTreeItemSelect(
                    {
                        value: currentItem.value,
                        displayValue: currentItem.displayValue,
                        key: currentItem.key
                    },
                    !props.options.allowMultiselection
                );
            } else if (searchString.length > 0 && shownItems.current != null) {
                if (!props.options.allowMultiselection) {
                    if (currentItemValue !== CLEAR_SELECTIONS_VALUE) {
                        processValueChange([{ ...currentItem }]);
                    } else if (shownItems.current.length > 0) {
                        processValueChange([shownItems.current[0]]);
                    }

                    processOnClose();
                } else if (matches.current.length > 0) {
                    const newValues = values != null ? [...values] : [];

                    matches.current.forEach((match) => {
                        const valueIndex = newValues.findIndex((value) => {
                            return match.value === value?.value;
                        });

                        if (valueIndex < 0) {
                            newValues.push({ ...match });
                        }
                    });

                    processValueChange(newValues);
                    searchFieldRef.current.value = '';
                    setSearchString('');
                }
            }
        }
    };

    // Use debounce to avoid rapid fire while the user is typing.
    // The UI crashes when not using debounce, due to searching through
    // and expanding all nodes on each key press.
    const handleOnSearchChange = React.useCallback(
        _.debounce((value: string) => {
            focusedItem.current = null;
            setSearchString(value);
        }, 500),
        []
    );

    const makeSearchFieldFocused = React.useCallback(() => {
        if (!options.hideSearchField) {
            focusedItem.current = null;
            searchFieldIsFocused.current = true;
            setUpdateFlag((updateFlag) => ++updateFlag);
        }
    }, [props.options.hideSearchField]);

    const handleOnSearchFieldBlur = React.useCallback(() => {
        searchFieldIsFocused.current = false;
    }, []);

    const handleOnToggle = React.useCallback(
        (treeItem: ITreeItem, item: ITreeNode, expand: boolean) => {
            const itemKey = treeItem.key;

            let forceUpdate = false;

            if (focusedItem.current == null || focusedItem.current.key !== itemKey) {
                focusedItem.current = treeItem;
                forceUpdate = true;
            }

            if (expandedStates.current[itemKey] !== expand) {
                expandedStates.current[itemKey] = expand;
                forceUpdate = true;
            }

            if (forceUpdate) {
                setUpdateFlag((updateFlag) => ++updateFlag);
            }

            if (expand && item.subNodesAreNotLoaded && props.options?.onLoadSubNodes != null) {
                props.options.onLoadSubNodes(item);
            }
        },
        [props.options?.onLoadSubNodes]
    );

    const moveFocus = React.useCallback(
        (direction: 'up' | 'down' | 'left' | 'right'): boolean => {
            const items = shownItems.current;

            if (items == null || items.length === 0) {
                return false;
            }

            if (searchFieldIsFocused.current && direction !== 'down') {
                return false;
            }

            let newFocusedItem: ITreeItem;
            let forceUpdate = false;

            if (searchFieldIsFocused.current) {
                if (direction === 'down') {
                    newFocusedItem = items[0];
                    forceUpdate = true;
                } else {
                    return false;
                }
            }

            const oldFocusedItem = focusedItem.current;

            if (newFocusedItem == null) {
                newFocusedItem = oldFocusedItem ?? items[0];

                const newKey = newFocusedItem.key;

                if (newKey === items[0].key && direction === 'up') {
                    makeSearchFieldFocused();
                    return false;
                }

                for (let i = 0; i < items.length; i++) {
                    const itemKey = items[i].key;

                    if (newKey === itemKey) {
                        switch (direction) {
                            case 'up':
                                if (i > 0) {
                                    newFocusedItem = items[i - 1];
                                }
                                break;

                            case 'down':
                                if (i < items.length - 1) {
                                    newFocusedItem = items[i + 1];
                                }
                                break;

                            case 'left':
                                expandedStates.current[itemKey] = false;
                                forceUpdate = true;
                                break;

                            case 'right':
                                expandedStates.current[itemKey] = true;

                                if (props.options?.onLoadSubNodes != null) {
                                    const treeNode = findTreeNodeByKey(itemKey, valuesState.valuesTree.tree);

                                    if (treeNode != null && treeNode.subNodesAreNotLoaded) {
                                        props.options.onLoadSubNodes(treeNode);
                                    }
                                }

                                forceUpdate = true;
                                break;
                        }

                        break;
                    }
                }
            }

            if (newFocusedItem == null || newFocusedItem.key !== oldFocusedItem?.key) {
                focusedItem.current = newFocusedItem;
                forceUpdate = true;
            }

            if (forceUpdate) {
                setUpdateFlag((updateFlag) => ++updateFlag);
            }

            return true;
        },
        [makeSearchFieldFocused, valuesState]
    );

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        switch (e.key) {
            case 'Tab':
                e.stopPropagation();
                e.preventDefault();
                break;

            case 'Escape':
                processOnClose();
                break;

            case 'Enter':
                processOnEnterPress();
                break;

            case 'ArrowUp':
                e.preventDefault();
                moveFocus('up');
                break;

            case 'ArrowDown':
                e.preventDefault();
                moveFocus('down');
                break;

            case 'ArrowLeft':
                if (moveFocus('left')) {
                    e.preventDefault();
                }
                break;

            case 'ArrowRight':
                if (moveFocus('right')) {
                    e.preventDefault();
                }
                break;

            case 'PageDown':
            case 'PageUp':
            case 'ScrollLock':
            case 'CapsLock':
            case 'Pause':
                e.preventDefault();
                break;

            default:
                if (!searchFieldIsFocused.current) {
                    e.stopPropagation();
                    e.preventDefault();

                    if (searchString === '' && e.code === 'Space') {
                        makeSearchFieldFocused();
                        return;
                    }

                    if (e.altKey || e.ctrlKey || e.metaKey) {
                        return;
                    }

                    if (
                        e.code === 'Home' ||
                        e.code === 'Insert' ||
                        e.code === 'End' ||
                        e.code === 'Delete' ||
                        e.code === 'NumLock'
                    ) {
                        return;
                    }

                    if (e.key !== '.' && e.key !== '/' && e.key !== '*' && e.key !== '-' && e.key !== '+') {
                        // For any Numpad keys except Numpad numbers.
                        if (e.code.indexOf('Numpad') === 0 && Number.isNaN(Number.parseInt(e.key))) {
                            return;
                        }
                    }

                    // For F1 - F12.
                    if (/^F(1[0-2]?|[1-9])$/.test(e.code)) {
                        return;
                    }

                    const input = searchFieldRef.current;

                    if (input != null) {
                        if (!e.shiftKey || e.key !== 'Shift') {
                            if (e.code === 'Backspace') {
                                input.value = input.value.slice(0, -1);
                            } else {
                                input.value += e.key;
                            }
                        }

                        handleOnSearchChange(input.value);
                    }
                }
        }
    };

    const getEmptyItemElement = React.useCallback(() => {
        shownItems.current = [];
        focusedItem.current = null;

        return (
            <li key="empty" ref={emptyItemRef} tabIndex={0}>
                <NoItemElement />
            </li>
        );
    }, []);

    const { options } = props;
    const { theme, notSelectableParentNodes } = options;

    const getContent = () => {
        const { valuesTree } = valuesState;

        if (valuesTree == null || valuesTree.tree == null) {
            return getEmptyItemElement();
        }

        const isSearching = searchString.length > 0;
        const threshold = isSearching ? searchListThreshold : listThreshold;

        let itemsToShow = valuesTree.tree as ITreeSearchNode[];

        matches.current = [];

        if (isSearching) {
            const stringToSearch = searchString.toLowerCase(); //searchString.replace(/^\s+|\s+$/g, '').toLowerCase();
            const searchFromStartOnly = theme?.[themeVars.itemSelector_SearchFromStartOnly];
            const searchResult = searchInTreeNodes(
                itemsToShow,
                stringToSearch,
                searchFromStartOnly,
                matches.current,
                notSelectableParentNodes
            );

            if (searchResult.length > 0) {
                itemsToShow = searchResult;
            } else {
                return getEmptyItemElement();
            }
        }

        let selectedNodes: ITreeItem[];

        if (values != null && values.length > 0) {
            selectedNodes = [];
            values.forEach((selectedNode) => {
                if (findTreeNodeByID(selectedNode.value, itemsToShow) != null) {
                    selectedNodes.push(selectedNode);
                }
            });

            if (selectedNodes.length === 0) {
                selectedNodes = null;
            }
        }

        const truncationResult = truncateItemsToShow(
            itemsToShow,
            threshold,
            isSearching,
            expandedStates.current,
            selectedNodes
        );

        itemsToShow = truncationResult.itemsToShow;

        let currentFocusedItem = focusedItem.current;

        if (currentFocusedItem != null) {
            if (
                currentFocusedItem.value !== CLEAR_SELECTIONS_VALUE &&
                currentFocusedItem.value !== SHOW_MORE_VALUE &&
                findTreeNodeByKey(currentFocusedItem.key, itemsToShow) == null
            ) {
                currentFocusedItem = null;
            }
        }

        const { allowNull, boldParentNodes, allowMultiselection } = options;
        const searchHighlightingColor = getSearchHighlightingColor(theme);
        const treeLength = itemsToShow.length;
        const result: React.JSX.Element[] = [];

        shownItems.current = [];

        for (let i = 0; i < treeLength; i++) {
            const item = itemsToShow[i];
            const id = item.id != null ? item.id : i.toString();
            const title = item.title;
            const itemKey = item.key;

            // Expand the only root node. Other nodes are expanded by selected item.
            if (treeLength === 1) {
                expandedStates.current[id] = true;
            }

            let focusedItemKey: string;

            if (currentFocusedItem != null) {
                focusedItemKey = currentFocusedItem.key;
            } else if (!searchFieldIsFocused.current) {
                focusedItemKey = itemKey;
                focusedItem.current = currentFocusedItem = {
                    value: id,
                    displayValue: title,
                    key: itemKey
                };
            }

            result.push(
                <TreeSelectItem
                    key={itemKey}
                    itemKey={itemKey}
                    id={id}
                    parentID={null}
                    title={title}
                    item={item}
                    match={item}
                    searchHighlightingColor={searchHighlightingColor}
                    references={itemsRef.current}
                    expandedStates={expandedStates.current}
                    expandAll={isSearching}
                    selectedNodes={selectedNodes}
                    focusedItemKey={focusedItemKey}
                    boldParentNodes={boldParentNodes}
                    notSelectableParentNodes={notSelectableParentNodes}
                    shownItems={shownItems.current}
                    allowMultiselection={allowMultiselection}
                    theme={theme}
                    onToggle={handleOnToggle}
                    onSelect={handleOnTreeItemSelect}
                    onCheckedChanged={handleOnCheckedChanged}
                />
            );
        }

        if (allowMultiselection && !options.hideSelectAllAndClear) {
            result.unshift(
                <ClearSelectionsItem
                    key="clear-selections"
                    values={values}
                    shownItems={shownItems.current}
                    searchString={searchString}
                    allowNull={allowNull}
                    focusedItemKey={focusedItem.current?.key}
                    references={itemsRef.current}
                    onClick={clearSelections}
                />
            );
        }

        if (truncationResult.visibleItemsCount > threshold) {
            result.push(
                <ShowMoreItem
                    key="show-more"
                    numberOfHiddenItems={truncationResult.totalCount - threshold}
                    shownItems={shownItems.current}
                    focusedItemKey={focusedItem.current?.key}
                    references={itemsRef.current}
                    onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                        processOnShowMoreClick(isSearching);
                    }}
                />
            );
        }

        return result;
    };

    let content: React.JSX.Element | React.JSX.Element[];

    if (valuesState.loading) {
        content = (
            <li key="dropdown-menu-content">
                <ProgressBar />
            </li>
        );
    } else {
        content = getContent();
    }

    let cotainerStyle: React.CSSProperties;

    if (theme != null) {
        const background = theme[themeVars.itemSelector_ItemListBackgroundColor];

        if (background != null) {
            cotainerStyle = {
                background: background
            };
        }
    }

    let searchField: React.JSX.Element;

    if (!options.hideSearchField) {
        searchField = (
            <SearchField
                reference={searchFieldRef}
                defaultValue={options.defaultSearchString}
                onFocus={makeSearchFieldFocused}
                onBlur={handleOnSearchFieldBlur}
                onChange={handleOnSearchChange}
            />
        );
    }

    return (
        <div
            className="tree-select-popup"
            style={cotainerStyle}
            onClick={handleOnContainerClick}
            onKeyDown={handleOnKeyDown}
        >
            {searchField}
            <ul ref={treeControlRef}>{content}</ul>
        </div>
    );
}

function normalizeNode(node: IDisplayValue, valuesTree: ITreeValues): IDisplayValue {
    let value = node.value;
    let displayValue = node.displayValue;

    if (value != null && displayValue == null) {
        if (valuesTree != null) {
            displayValue = valuesTree.nodes[value]?.title || null;
        }
    }

    if (value != null) {
        if (typeof value !== 'string') {
            value = (value as any).toString();
        }

        return {
            value: value,
            displayValue: displayValue
        };
    }

    return null;
}

function normalizeValue(
    value: IDisplayValue | IDisplayValue[],
    allowMultiselection: boolean,
    treeValues: ITreeValues
): IDisplayValue[] {
    if (value != null) {
        if (Array.isArray(value)) {
            if (allowMultiselection) {
                const result: IDisplayValue[] = [];

                value.forEach((node) => {
                    const normalizedNode = normalizeNode(node, treeValues);

                    if (normalizedNode != null) {
                        result.push(normalizedNode);
                    }
                });

                if (result.length > 0) {
                    return result;
                }
            } else if (value.length > 0) {
                const normalizedNode = normalizeNode(value[0], treeValues);

                if (normalizedNode != null) {
                    return [normalizedNode];
                }
            }
        } else {
            const normalizedNode = normalizeNode(value, treeValues);

            if (normalizedNode != null) {
                return [normalizedNode];
            }
        }
    }

    return [];
}

function processOnChange(newValue: IDisplayValue[], onChange: (value: IDisplayValue[]) => void) {
    if (onChange != null) {
        onChange(newValue != null ? [...newValue] : null);
    }
}

function valueIsEmpty(value: IDisplayValue | IDisplayValue[]) {
    return value == null || (Array.isArray(value) && value.length === 0);
}

export default function TreeSelect(props: ITreeSelectProperties) {
    const updateDropdownState = React.useRef<(state: IValuesState) => void>(null);
    const gettingValuesDeffered = React.useRef<JQueryDeferred<ITreeValues>>(null);
    const expandedStates = React.useRef<IExpandedStates>({});

    const [valuesTree, setValuesTree] = React.useState<ITreeValues>();
    const stateObj = useDropdownSelectorStateHook(normalizeValue(props.value, props.allowMultiselection, valuesTree));
    const { state, setValue, makeValueNotChanged } = stateObj;
    const [ignoreBackendCache, setIgnoreBackendCache] = React.useState(false);
    const [, setUpdateFlag] = React.useState(0);

    const getValues = React.useCallback(
        (loadAll?: boolean): JQueryDeferred<ITreeValues> => {
            if (props.globalFilter != null) {
                return getTreeValuesFilteredByGlobalFilter(
                    props.dataSourceID,
                    props.attributeData,
                    props.globalFilter,
                    props.localFilter,
                    props.sortingType,
                    loadAll,
                    ignoreBackendCache,
                    props.theme,
                    props.glob
                ).done(() => {
                    if (ignoreBackendCache) {
                        setIgnoreBackendCache(false);
                    }
                });
            } else {
                return props.getValues();
            }
        },
        [
            ignoreBackendCache,
            props.dataSourceID,
            props.attributeData,
            props.globalFilter,
            props.localFilter,
            props.sortingType,
            props.theme,
            props.getValues
        ]
    );

    const selectFirstItemByDefault = React.useMemo(
        () => props.selectFirstItemByDefault && props.allowMultiselection === false,
        [props.selectFirstItemByDefault && props.allowMultiselection]
    );

    React.useEffect(() => {
        const handleOnCacheClearedChange = () => {
            setIgnoreBackendCache(true);
        };

        if (selectFirstItemByDefault && valueIsEmpty(props.value)) {
            getValues(true).done((treeValues) => {
                let newValue = normalizeValue(props.value, props.allowMultiselection, treeValues);

                if (valueIsEmpty(newValue)) {
                    newValue = getFirstTreeItem(treeValues, selectFirstItemByDefault);
                }

                setValue(newValue, true);
                processOnChange(newValue, props.onChange);
            });
        }

        props.glob.EntityModelsStore.addOnCacheClearedListener(handleOnCacheClearedChange);
        setUpdateFlag((updateFlag) => ++updateFlag);

        return () => {
            props.glob.EntityModelsStore.removeOnCacheClearedListener(handleOnCacheClearedChange);
        };
    }, [selectFirstItemByDefault]);

    const handleOnChange = React.useCallback(
        (value: IDisplayValue[]) => {
            setValue(value, true);
            processOnChange(value, props.onChange);
        },
        [props.onChange]
    );

    React.useEffect(() => {
        let displayValueIsEmpty = false;

        if (props.value != null) {
            if (Array.isArray(props.value)) {
                displayValueIsEmpty = _.some(props.value, (node) => {
                    return node.value != null && node.displayValue == null;
                });
            } else {
                displayValueIsEmpty = props.value.value != null && props.value.displayValue == null;
            }
        }

        if (
            displayValueIsEmpty ||
            (selectFirstItemByDefault && (props.allowNull ?? props.options?.allowNull) === false)
        ) {
            // URL Link with a filter to a tree attribute a value with ID, but without Display Value.
            // In such case all values are loaded to ensure that value it not filtered out.
            getValues(true).done((treeValues) => {
                let newValue = normalizeValue(props.value, props.allowMultiselection, treeValues);

                if (valueIsEmpty(newValue)) {
                    newValue = getFirstTreeItem(treeValues, selectFirstItemByDefault);
                }

                setValue(newValue, true);

                if (
                    selectFirstItemByDefault &&
                    Array.isArray(newValue) &&
                    Array.isArray(props.value) &&
                    newValue[0]?.value !== props.value[0]?.value
                ) {
                    // On filter reset.
                    handleOnChange(newValue);
                }
            });
        } else {
            const newValue = normalizeValue(props.value, props.allowMultiselection, valuesTree);

            setValue(newValue, true);
        }
    }, [
        props.value,
        selectFirstItemByDefault,
        props.allowMultiselection,
        props.allowNull ?? props.options?.allowNull,
        handleOnChange,
        getValues
    ]);

    const handleOnDropDownChange = React.useCallback((value: IDisplayValue[]) => {
        setValue(value, false);
    }, []);

    const handleOnDropDownClose = React.useCallback(
        (newExpandedStates: IExpandedStates) => {
            if (gettingValuesDeffered.current != null && gettingValuesDeffered.current.reject != null) {
                gettingValuesDeffered.current.reject(true);
            }

            updateDropdownState.current = null;
            gettingValuesDeffered.current = null;
            expandedStates.current = newExpandedStates;
            state.dropDownIsVisible = false;

            if (props.onChange != null) {
                const dataChanged = !compareObjects(state.oldValue, state.value);

                if (dataChanged) {
                    makeValueNotChanged();
                    processOnChange(state.value, props.onChange);
                }
            }
        },
        [props.onChange]
    );

    const handleDropdownLoadingSync = React.useCallback((def: JQueryDeferred<ITreeValues>) => {
        if (def == null) {
            throw new Error('props.getValues must return a Deferred value');
        }

        gettingValuesDeffered.current = def
            .done((valuesTree) => {
                setValuesTree(valuesTree);

                if (updateDropdownState.current == null) {
                    return;
                }

                updateDropdownState.current({
                    valuesTree: valuesTree,
                    loading: false
                });
            })
            .fail((cancel) => {
                if (cancel) {
                    return;
                }

                if (updateDropdownState.current == null) {
                    return;
                }

                updateDropdownState.current({
                    loading: false
                });
            });
    }, []);

    const isGetValuesSet = React.useCallback(() => {
        return props.getValues != null || props.globalFilter != null;
    }, [props.getValues, props.globalFilter]);

    const handleDropdownInit = React.useCallback(() => {
        if (isGetValuesSet()) {
            handleDropdownLoadingSync(getValues());
        }
    }, [isGetValuesSet, getValues]);

    const dropdownStateUpdateHook = React.useCallback((callback: (obj) => void) => {
        updateDropdownState.current = (obj) => {
            callback(obj);
        };
    }, []);

    const handleOnLoadSubNodes = React.useCallback((item: ITreeNode) => {
        props.loadSubNodes?.(item).done(() => {
            handleDropdownInit();
        });
    }, []);

    if (props.showTreeOnly) {
        const loading = isGetValuesSet();
        const options: IDropdownOptions = {
            value: state.value,
            expandedStates: expandedStates.current,

            listThreshold: props.listThreshold,
            boldParentNodes: props.boldParentNodes,
            notSelectableParentNodes: props.notSelectableParentNodes,
            autoSelectChildNodes: props.autoSelectChildNodes,
            selectFirstItemByDefault: props.selectFirstItemByDefault,
            allowMultiselection: props.allowMultiselection,
            allowNull: props.allowNull,
            defaultSearchString: undefined,
            hideSelectAllAndClear: props.hideSelectAllAndClear,
            hideSearchField: props.hideSearchField,
            loading: loading,
            theme: props.theme,

            updateState: dropdownStateUpdateHook,
            onInit: handleDropdownInit,
            onChange: handleOnChange,
            onLoadSubNodes: handleOnLoadSubNodes
        };

        return (
            <div className="tree-select">
                <TreeDropdown options={options} zIndex={1} />
            </div>
        );
    }

    const handleOnDropdownShow = (position: IPosition, size: ISize<number>, defaultSearchString: string) => {
        const loading = isGetValuesSet();

        props.glob.DialogActions.createPopup<IDropdownOptions>('', {
            value: state.value,
            name: props.name,
            top: position.top,
            left: position.left,
            minWidth: size.width,
            width: size.width,
            parentComponentHeight: size.height,
            listThreshold: props.listThreshold,
            loading: loading,
            boldParentNodes: props.boldParentNodes,
            notSelectableParentNodes: props.notSelectableParentNodes,
            autoSelectChildNodes: props.autoSelectChildNodes,
            selectFirstItemByDefault: props.selectFirstItemByDefault,
            expandedStates: expandedStates.current,
            allowMultiselection: props.allowMultiselection,
            allowNull: props.allowNull,
            defaultSearchString: defaultSearchString,
            hideSelectAllAndClear: props.hideSelectAllAndClear,
            hideSearchField: props.hideSearchField,
            theme: props.theme,
            updateState: dropdownStateUpdateHook,
            onInit: handleDropdownInit,
            onChange: handleOnDropDownChange,
            onLoadSubNodes: handleOnLoadSubNodes,
            onClose: handleOnDropDownClose,

            component: TreeDropdown
        });
    };

    const options = props.options ?? {};

    return (
        <div className="tree-select">
            <SelectedItemsDisplayStatelessComponent
                mainComponent={true}
                stateObj={stateObj}
                keySelect={true}
                allowNull={props.allowNull ?? options.allowNull}
                allowReordering={false}
                singleSelectMode={!props.allowMultiselection}
                listThreshold={props.listThreshold}
                shortView={true}
                placeholder={props.placeholder}
                usePlaceholderAsTitle={props.usePlaceholderAsTitle}
                tabIndex={props.tabIndex}
                width={props.width ?? options.width}
                minWidth={props.minWidth ?? options.minWidth}
                readOnly={props.readOnly ?? options.readOnly}
                theme={props.theme}
                name={props.name}
                minSelectedItemsCellWidth={props.minSelectedItemsCellWidth}
                glob={props.glob}
                onChange={props.onChange}
                onCustomDropDownShow={handleOnDropdownShow}
            />
        </div>
    );
}

export class TreeSelectField extends ParameterFieldBase<IParameterFieldBaseOptions> {
    public static EditComponent = TreeSelect;
    public getDefaultDirectOptions() {
        return {};
    }

    constructor(
        glob: IGlob,
        title: string,
        validators?: IValidator[],
        defaultVal: any = {},
        options?: IParameterFieldBaseOptions,
        directOptions?: ITreeSelectProperties
    ) {
        super(glob, title, [], validators, defaultVal, options, directOptions);
    }
}
